/**
 * TabbedSection — a generic accessible tabs custom element.
 *
 * Responsibilities:
 *   - Tab switching
 *   - Keyboard navigation (ArrowLeft, ArrowRight, Home, End)
 *   - Roving tabindex
 *   - Managing aria-selected, aria-controls, aria-labelledby
 *   - Showing / hiding panels via the `hidden` attribute
 *
 * This component is completely generic. It knows nothing about products,
 * collections, articles, or card types — it only switches panels.
 *
 * DOM contract:
 *   [role="tablist"]              — the container holding all tabs
 *   [role="tab"]                  — each tab button (inside the tablist)
 *   [data-tab-panel]              — each panel (value = index, 0-based)
 *   aria-controls on each tab     — points to the panel id
 *   aria-labelledby on each panel — points to the tab id
 *
 * The snippet (tabbed-section.liquid) sets all required ARIA attributes
 * in HTML so the component works even without JavaScript.
 */

class TabbedSection extends HTMLElement {
  /** @type {number} */
  #activeIndex = 0;

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  // ─── DOM refs ──────────────────────────────────────────────────────────────

  get #tablist() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[role='tablist']"));
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll("[role='tab']")));
  }

  get #panels() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll("[data-tab-panel]")));
  }

  // ─── Setup / teardown ──────────────────────────────────────────────────────

  #setup() {
    const tablist = this.#tablist;
    if (!tablist) return;

    tablist.addEventListener("click", this.#handleClick);
    tablist.addEventListener("keydown", this.#handleKeydown);

    // Determine initial active tab from aria-selected="true" in markup,
    // or default to the first tab.
    const tabs = this.#tabs;
    const preselected = tabs.findIndex((t) => t.getAttribute("aria-selected") === "true");
    this.#activateTab(preselected >= 0 ? preselected : 0, false);
  }

  #teardown() {
    const tablist = this.#tablist;
    tablist?.removeEventListener("click", this.#handleClick);
    tablist?.removeEventListener("keydown", this.#handleKeydown);
  }

  // ─── Tab activation ────────────────────────────────────────────────────────

  /**
   * Activate the tab at the given index.
   * @param {number} index
   * @param {boolean} [moveFocus=true] - whether to move focus to the activated tab
   */
  #activateTab(index, moveFocus = true) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    if (!tabs.length) return;

    const clamped = Math.max(0, Math.min(index, tabs.length - 1));
    this.#activeIndex = clamped;

    tabs.forEach((tab, i) => {
      const isActive = i === clamped;
      tab.setAttribute("aria-selected", String(isActive));
      // Roving tabindex: only the active tab is in the tab order
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
    });

    panels.forEach((panel) => {
      const panelIndex = parseInt(panel.dataset.tabPanel ?? "-1", 10);
      if (panelIndex === clamped) {
        panel.removeAttribute("hidden");
      } else {
        panel.setAttribute("hidden", "");
      }
    });

    if (moveFocus) {
      tabs[clamped]?.focus();
    }

    /** @type {any} */ (this.closest("card-carousel"))?.refresh();
  }

  // ─── Handlers ──────────────────────────────────────────────────────────────

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const tab = /** @type {HTMLElement | null} */ (/** @type {Element} */ (event.target).closest("[role='tab']"));
    if (!tab) return;
    const index = this.#tabs.indexOf(tab);
    if (index >= 0) this.#activateTab(index);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    if (!tabs.length) return;

    let nextIndex = this.#activeIndex;

    switch (event.key) {
      case "ArrowLeft":
        nextIndex = (this.#activeIndex - 1 + tabs.length) % tabs.length;
        break;
      case "ArrowRight":
        nextIndex = (this.#activeIndex + 1) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(nextIndex);
  };
}

if (!customElements.get("tabbed-section")) {
  customElements.define("tabbed-section", TabbedSection);
}
